import round from "lodash/round";
import moment from "moment";
import { getAllSetsCounts } from "../../../../utils/actuals";

// CONSTANTS

export const standardMeasures = {
  neck: 0,
  chest: 0,
  leftArm: 0,
  rightArm: 0,
  waist: 0,
  hips: 0,
  leftThigh: 0,
  rightThigh: 0,
  leftCalf: 0,
  rightCalf: 0,
  glutes: 0,
};
export const noDataSymbol = "-";
// Date formats
export const utcFormat = "YYYYMMDD";
export const bodyWeightFormat = "MMM D, YYYY";
export const defaultFormat = "YYYY-MM-DD";

export const formatWeekCases = (selectedWeek, weeksNumber) => {
  switch (selectedWeek) {
    case 6:
      return 1;
    case 7:
      return weeksNumber === 4 ? 4 : 5;
    default:
      return selectedWeek;
  }
};

export const getSelectedWeek = (
  selectedWeek,
  defaultYear,
  defaultMonth,
  calendarDay
) => {
  if (selectedWeek === 7) {
    // Generate weeks from the previous month
    const previousMonth = moment({
      year: defaultYear,
      month: defaultMonth,
    }).subtract(1, "month");

    const weeks = generateWeeks(
      previousMonth.year(),
      previousMonth.month(),
      calendarDay
    );
    const result = {
      weeksNumber: weeks.length,
      week: weeks.filter((week) => week.number === weeks.length),
    };

    return result;
  } else if (selectedWeek === 6) {
    const month = moment({ year: defaultYear, month: defaultMonth })
      .startOf("M")
      .add(1, "month");

    const weeks = generateWeeks(month.year(), month.month(), calendarDay);

    const result = {
      weeksNumber: weeks.length,
      week: weeks.filter((week) => week.number === 1),
    };

    return result;
  }

  const weeks = generateWeeks(defaultYear, defaultMonth, calendarDay);

  const result = {
    weeksNumber: weeks.length,
    week: weeks.filter((week) => week.number === selectedWeek),
  };

  return result;
};

export const generateWeeks = (startYear, startMonth, chosenDayOfWeek) => {
  const startDate = moment({ year: startYear, month: startMonth }).startOf(
    "month"
  );
  const firstDayOfWeek = (startDate.isoWeekday() - chosenDayOfWeek + 7) % 7;
  const weeks = [];

  // Find the first occurrence of the chosen day in the given month
  let currentDate = startDate.clone().subtract(firstDayOfWeek, "days");
  while (currentDate.month() < startMonth) {
    currentDate.add(7, "days");
  }

  // Adjust the start date to the chosen day of week
  if (
    currentDate.month() === startMonth &&
    currentDate.date() < startDate.date()
  ) {
    currentDate.add(7, "days");
  }

  // Generate weeks for the remaining part of the month
  for (let i = 0; i < 6; i++) {
    const weekStart = currentDate.clone();
    const weekEnd = weekStart.clone().add(6, "days");
    weeks.push({ start: weekStart, end: weekEnd, number: i + 1 });
    currentDate = currentDate.add(7, "days");
    if (currentDate.month() !== startMonth) {
      break;
    }
  }

  return weeks;
};

export const getCurrentMonthAndYear = (startDay, endDay) => {
  const startDate = moment(startDay);
  const startMonth = startDate.month();
  const startYear = startDate.year();
  const endDate = moment(endDay);

  // Check if the end date is in a different month or year than the start date
  if (endDate.month() !== startMonth || endDate.year() !== startYear) {
    // If the end date is in a later month, use the start date month and year
    if (endDate.isAfter(startDate)) {
      return { month: startMonth, year: startYear };
    }
    // If the end date is in an earlier month, use the previous month and the start date year
    else {
      return { month: (startMonth - 1 + 12) % 12, year: startYear };
    }
  }
  // If the end date is in the same month and year as the start date, use the start date month and year
  else {
    return { month: startMonth, year: startYear };
  }
};

export const mapBodyWeights = (data, unitMultiplier) => ({
  date: moment(String(data.utcDate)).format(bodyWeightFormat),
  bodyWeight: data.bodyWeight ? round(data.bodyWeight * unitMultiplier, 1) : 0,
  utcDate: data.utcDate,
});

export const getBodyMeasures = (data, units, useLastMeasure) => {
  if (useLastMeasure) {
    const bodyMeasuresData = data.flatMap(({ bodyMeasurements, utcDate }) =>
      bodyMeasurements ? { bodyMeasurements, utcDate } : []
    );

    let measuresByPart = { ...standardMeasures };

    Object.keys(standardMeasures).forEach((key) => {
      measuresByPart = {
        ...measuresByPart,
        [key]: bodyMeasuresData
          .filter(
            (item) =>
              item.bodyMeasurements[key] &&
              item.bodyMeasurements[key] !== null &&
              item.bodyMeasurements[key] > 0
          )
          .sort((a, b) => b.utcDate - a.utcDate)[0]?.bodyMeasurements[key],
      };
    });
    return measuresByPart;
  }
  const onlyBodyMeasures = data.flatMap(({ bodyMeasurements }) =>
    bodyMeasurements ? bodyMeasurements : []
  );

  const bodyMeasurements = onlyBodyMeasures.reduce((acc, curr) => {
    Object.keys(curr).forEach((key) => {
      if (curr[key] !== "" && parseFloat(curr[key]) !== 0) {
        acc[key] = acc[key]
          ? {
              sum: acc[key].sum + parseFloat(curr[key]),
              count: acc[key].count + 1,
            }
          : { sum: parseFloat(curr[key]), count: 1 };
      }
    });
    return acc;
  }, {});

  const avgBodyMeasurements = {};
  const unitMultiplier = units === "metric" ? 1 : 2.54;
  Object.keys(bodyMeasurements).forEach((key) => {
    const { sum, count } = bodyMeasurements[key];
    avgBodyMeasurements[key] = round(sum / count / unitMultiplier, 1);
  });

  return avgBodyMeasurements;
};

export const formatBodyMeasure = (key) => {
  const words = key.split(/(?=[A-Z])/);
  return words
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};
export const mapMeasures = (key, measures) => {
  return {
    part: key,
    measure: measures[key],
    gain: 0,
  };
};

export const mapDayRanges = (date) => {
  // Get the day of the week and number of the day as strings
  const dayOfWeek = date.format("ddd"); // e.g. "Mon", "Tue", "Wed"
  const dayOfMonth = date.format("D"); // e.g. "1", "2", "3"

  // Return an object with `day` and `number` properties
  return { day: dayOfWeek, number: parseInt(dayOfMonth), date };
};

export const getDaysRange = (start, end) => {
  const startDate = moment(start); // set start date
  const endDate = moment(end); // set end date

  const datesArray = [];
  let currentDate = startDate.clone();
  while (datesArray.length < 7 && startDate <= endDate) {
    datesArray.push(currentDate);
    currentDate = currentDate.clone().add(1, "day");
  }
  return datesArray;
};

export const findFrontLeftBack = (objects) => {
  const result = {};
  //clean undefined values in objects
  objects = objects.filter((obj) => obj !== undefined);
  for (const obj of objects) {
    if (obj.hasOwnProperty("front")) {
      result.front = obj.front.s3Path;
    }
    if (obj.hasOwnProperty("back")) {
      result.back = obj.back.s3Path;
    }
    if (obj.hasOwnProperty("side")) {
      result.side = obj.side.s3Path;
    }
  }
  return result;
};

export const mapPhotos = (data) => data.bodyPhotos;

export const getDateFromWeek = (week, key) => {
  return week[key].format(defaultFormat);
};

export const formatWeekNumber = (selectedWeek) =>
  selectedWeek ? formatWeekCases(selectedWeek) : 1;

export const determineAvg = (data, key) => {
  const filteredStats = data.filter((stat) => !!stat[key]);

  const result =
    round(
      filteredStats.reduce((acc, prev) => acc + Number(prev[key]), 0) /
        filteredStats.length,
      0
    ) || 0;

  return result;
};

export const formatAreaChart = (data, targetValue) => ({
  date: data.date,
  value: Number(data[targetValue]),
});

export const mapBodyMeasureChart = (data, key, units) => {
  const unitMultiplier = units === "metric" ? 1 : 2.54;

  return {
    date: moment(String(data.utcDate)).format(bodyWeightFormat),
    value: round(data.bodyMeasurements[key] / unitMultiplier, 1),
  };
};

export const mapWorkout = (activeExercises, stat) => {
  const exercise = activeExercises.find((ex) => ex.utcDate === stat.utcDate);
  stat.work = exercise ? findMatchedKey(getAllSetsCounts(exercise)) : "rest";

  stat.exercise = exercise;
  return stat;
};

//with start and end date, generate a range of dates based on that start.
//start and end are utc date, so we need to convert them to moment object
export const getDatesRange = (start, end) => {
  const startDate = moment(start); // set start date
  const endDate = moment(end); // set end date

  const datesArray = [];
  let currentDate = startDate.clone();
  while (currentDate <= endDate) {
    //current date need to be transormed to utc date
    datesArray.push({
      utcDate: parseInt(currentDate.format("YYYYMMDD")),
      work: "rest",
      date: currentDate,
    });
    currentDate = currentDate.clone().add(1, "day");
  }
  return datesArray;
};

export const mapDaysOfTheWeek = (data) => {
  const { utcDate, sets } = data;
  const day = moment.utc(utcDate, "YYYYMMDD").format("ddd");
  const work = determineWorkStatus(sets);
  return { day, work };
};

const determineWorkStatus = (sets) => {
  if (!sets || sets.length === 0) {
    return "rest";
  }

  for (const set of sets) {
    if (!set.outcomes) {
      return "missed";
    }

    for (const key in set.outcomes) {
      if (set.outcomes[key].status === "completed") {
        return "completed";
      }
    }
  }

  return "missed"; // if no completed sets were found
};

export const combineDays = (activeExercises) => {
  const days = new Map();

  for (const item of activeExercises) {
    const { day, work } = item;

    if (days.has(day)) {
      if (work === "completed") {
        days.set(day, "completed");
      } else if (days.get(day) !== "completed") {
        days.set(day, work);
      }
    } else {
      days.set(day, work);
    }
  }

  const combinedDays = [];

  for (const [day, work] of days) {
    combinedDays.push({ day, work });
  }

  return combinedDays;
};

export const countWorkout = (data) => {
  const completed = data.filter((day) => day.work === "completed").length;
  const rest = data.filter((day) => day.work === "rest").length;
  const missed = data.filter((day) => day.work === "missed").length;
  return { completed, rest, missed };
};

export const groupBodyMeasurements = (data) => {
  const formattedMeasures = {};
  const formattedChanges = {};
  let lastMeasures = null;

  data.forEach((item) => {
    const { bodyMeasurements: measures } = item;

    if (!lastMeasures) {
      lastMeasures = measures;
      return;
    }

    for (const [key, value] of Object.entries(measures)) {
      if (!formattedMeasures[key]) {
        formattedMeasures[key] = Number(value);
        formattedChanges[key] =
          formattedMeasures[key] -
          Number(lastMeasures[key] || formattedMeasures[key]);
      } else {
        formattedMeasures[key] += Number(value);
        formattedChanges[key] += Number(value) - Number(lastMeasures[key]);
      }
    }

    lastMeasures = measures;
  });

  for (const [key, value] of Object.entries(formattedMeasures)) {
    formattedMeasures[key] = value.toFixed(1);
    formattedChanges[key] = formattedChanges[key]
      ? formattedChanges[key].toFixed(1)
      : value.toFixed(1);
  }

  return { formattedMeasures, formattedChanges };
};

export const groupMeasurementsByType = (data) => {
  const groupedMeasurements = data.reduce((acc, curr) => {
    const measurements = curr.bodyMeasurements;
    Object.keys(measurements).forEach((key) => {
      const value = measurements[key];
      if (!acc[key]) {
        acc[key] = {
          measurements: [],
          change: [],
        };
      }
      acc[key].measurements.push(value);
      const length = acc[key].measurements.length;
      if (length > 1) {
        const change = value - acc[key].measurements[length - 2];
        acc[key].change.push(change);
      }
    });
    return acc;
  }, {});

  return groupedMeasurements;
};

export const processBodyMeasurements = (data, units) => {
  let formattedMeasures = {};
  let formattedChanges = {};
  let prevMeasures = {};
  const unitMultiplier = units === "metric" ? 1 : 2.54;

  data.forEach(({ bodyMeasurements }) => {
    if (!bodyMeasurements) return;

    Object.entries(bodyMeasurements).forEach(([key, value]) => {
      const roundedValue = round(value / unitMultiplier, 1);

      const prevValue = prevMeasures[key];
      if (prevValue === undefined) {
        formattedChanges[key] = 0;
      } else {
        const change = roundedValue - prevValue;
        formattedChanges[key] = round(change, 1);
      }
      formattedMeasures[key] = roundedValue;
    });

    prevMeasures = { ...bodyMeasurements };
  });

  return {
    formattedMeasures,
    formattedChanges,
  };
};

export const calculateBodyWeightChanges = (entries, unitMultiplier) => {
  let initialWeight = null;
  let totalWeightChange = 0;

  for (let i = 0; i < entries.length; i++) {
    let currentEntry = entries[i];

    if (currentEntry.bodyWeight) {
      let currentWeight = round(
        parseFloat(currentEntry.bodyWeight) * unitMultiplier,
        1
      );

      if (initialWeight !== null) {
        totalWeightChange += currentWeight - initialWeight;
      }

      initialWeight = currentWeight;
    }
  }

  return round(totalWeightChange * 10, 1) / 10;
};

export const getYearAndMonth = (date) => {
  const month = date ? moment(date).month() : moment().month();

  const year = date ? moment(date).year() : moment().year();
  return { month, year };
};

export const findMatchedKey = (obj) => {
  if (obj.total === obj.completed) {
    return "completed";
  } else if (obj.total === obj.missed) {
    return "missed";
  } else {
    return "missed";
  }
};

export const filterByWeekRange = (stat, { start, end }) => {
  return moment(stat.utcDate, defaultFormat).isBetween(
    moment(start, defaultFormat),
    moment(end, defaultFormat),
    null,
    "[]"
  );
};

//utc is a number from older to newest date
export const sortByUtcDate = (a, b) => {
  return new Date(a.date) - new Date(b.date);
};

export const filterNutrients = ({ nutrients }) => !!nutrients;

export const calculateAverage = (values) => {
  if (!values.length) {
    return 0;
  }

  const sumReducer = (acc, val) => acc + val || 0;
  const sum = values.reduce(sumReducer, 0);
  const average = sum / values.length;
  return round(average, 1);
};

export const getPreviousWeek = ({ start, end }) => {
  return {
    start: moment(start, defaultFormat).subtract(1, "week"),
    end: moment(end, defaultFormat).subtract(1, "week"),
  };
};

export const mapBodyWeight = ({ bodyWeight }) => bodyWeight;

export const findStartAndEndOfWeek = (stats) => {
  const start = stats[0].utcDate;
  const end = stats[stats.length - 1].utcDate;
  return { start, end };
};

export const filterZeroValue = (value) => value !== 0;
